import { trackPageView } from "@playlive/adobe-analytics";
import { lazy } from "react";
import { Outlet, Route } from "react-router";
import { TwitchCallbackPage } from "react-twitch-implicit-auth";
import GlobalErrorBoundary from "@/components/layout/global-error-boundary";
import Layout from "@/components/layout/layout";
import ScheduleLayout from "@/components/page-layouts/schedule.page-layout";
import { chatbots } from "@/data/chatbots";
import { dashboards } from "@/data/dashboards";
import { graphics } from "@/data/graphics";
import { information } from "@/data/information";
import { overlays } from "@/data/overlays";
import { getTypeSlug } from "@/util/common";
import TiltifyCallback from "./misc/tiltify-callback";
import TwitchChatbotCallbackPage from "./misc/twitch-chatbot-callback";
import ProfilePage from "./profile.page";

const DonationTrainOverlayPage = lazy(
  () => import("./overlays/donation-train/donation-train-overlay.page"),
);
const MaxProgressBarPage = lazy(() => import("./progress-bars/max-progress-bar.page"));

// Misc. Pages
const PageNotFound = lazy(() => import("./not-found"));
const Home = lazy(() => import("./home.page"));

// Layout
const CardPageLayout = lazy(() => import("@/components/page-layouts/card.page-layout"));
const ChatbotPageLayout = lazy(() => import("@/components/page-layouts/chatbot.page-layout"));
const GraphicsBundlePageLayout = lazy(
  () => import("@/components/page-layouts/graphics-bundle.page-layout"),
);
const InformationPageLayout = lazy(
  () => import("@/components/page-layouts/information.page-layout"),
);
const ListPageLayout = lazy(() => import("@/components/page-layouts/list.page-layout"));
const OverlayPageLayout = lazy(() => import("@/components/page-layouts/overlay.page-layout"));
const DashboardPageLayout = lazy(() => import("@/components/page-layouts/dashboard.page-layout"));

// Chatbot Pages
const DiscordChatbotPage = lazy(() => import("./chatbots/discord-chatbot.page"));
const TwitchChatbotPage = lazy(() => import("./chatbots/twitch-chatbot.page"));

// Information Pages
const BestPracticesPage = lazy(() => import("./information/best-practices.page"));
const MatchingPage = lazy(() => import("./information/matching.page"));
const EveryDollarCountsPage = lazy(
  () => import("./information/every-dollar-counts/every-dollar-counts.page"),
);

// Schedule Pages
const Schedule = lazy(() => import("./schedule/page"));

// Overlay Pages
const AlertsOverlayPage = lazy(() => import("./overlays/alerts/alerts-overlay.page"));
const ArtSpaceOverlayPage = lazy(() => import("./overlays/art-space/art-space-overlay.page"));
const BallPitOverlayPage = lazy(() => import("./overlays/ball-pit/ball-pit-overlay.page"));
const CCAMRibbonOverlayPage = lazy(() => import("./overlays/ccam-ribbon.page"));
const DashboardPage = lazy(() => import("./dashboards/fundraising/dashboard.page"));
const LeaderboardExclusionDashboardPage = lazy(
  () => import("./dashboards/leaderboard-exclusion/leaderboard-exclusion-dashboard.page"),
);
const DonationTrainDashboardPage = lazy(
  () => import("./dashboards/donation-train/donation-train-dashboard.page"),
);

const DonorListOverlayPage = lazy(() => import("./overlays/donor-list-overlay.page"));
const GiftsThatGiveOverlayPage = lazy(
  () => import("./overlays/gifts-that-give/gifts-that-give.page"),
);
const LeaderboardOverlayPage = lazy(() => import("./overlays/leaderboard-overlay.page"));
const MilestoneOverlayPage = lazy(() => import("./overlays/milestone-overlay.page"));
const PollOverlayPage = lazy(() => import("./overlays/poll-overlay.page"));
const RewardsOverlayPage = lazy(() => import("./overlays/rewards-overlay.page"));
const SpotlightOverlayPage = lazy(() => import("./overlays/spotlight-overlay.page.tsx"));
const TextToSpeechPage = lazy(() => import("./overlays/text-to-speech/text-to-speech.page"));
const TotalOverlayPage = lazy(() => import("./overlays/total-overlay.page"));
const TwitchChatOverlayPage = lazy(
  () => import("./overlays/twitch-chat/twitch-chat-overlay.page.tsx"),
);

const LifetimeRaisedPage = lazy(() => import("./overlays/lifetime-raised.page"));

const PreviousTotalsOverlayPage = lazy(() => import("./overlays/previous-years-overlay.page"));

const ScheduleOverlayPage = lazy(() => import("./overlays/schedule-overlay.page"));

const SubathonTimerPage = lazy(() => import("./overlays/subathon-timer/subathon-timer.page"));

const BasicProgressBarPage = lazy(() => import("./progress-bars/basic-progress-bar.page"));
const NewProgressBarPage = lazy(() => import("./progress-bars/new-progress-bar.page"));

const MinProgressBarPage = lazy(() => import("./progress-bars/min-progress-bar.page"));

const VerticalProgressBarPage = lazy(() => import("./progress-bars/vertical-progress-bar.page"));

const GCXSelfServicePage = lazy(() => import("./gcx/gcx-self-service.page"));

const FriendsgivingSelfServicePage = lazy(
  () => import("./friendsgiving/friendsgiving-self-service.page"),
);

const LevelUpSelfServicePage = lazy(() => import("./level-up/level-up-self-service.page"));

const CCAMToolsPage = lazy(() => import("./ccam-tools.page"));

const loader = async (args: { request: Request }) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const pageURL = new URL(args.request.url);
      const pathName = pageURL.pathname;

      const { type, slug } = getTypeSlug(pathName);
      let pageType: string | undefined;
      let data: Record<string, { name: string }> | undefined;

      switch (type) {
        case "overlays":
          data = overlays as unknown as Record<string, { name: string }>;
          pageType = "Overlays";
          break;
        case "chatbots":
          data = chatbots as unknown as Record<string, { name: string }>;
          pageType = "Chatbots";
          break;
        case "graphics":
          data = graphics as unknown as Record<string, { name: string }>;
          pageType = "Graphics";
          break;
        case "information":
          data = information as unknown as Record<string, { name: string }>;
          pageType = "Information";
          break;
        case "dashboards":
          data = dashboards as unknown as Record<string, { name: string }>;
          pageType = "Dashboards";
          break;
      }

      if (data && slug && data[slug]) {
        const { name } = data[slug];

        let fixedName = name;

        if (!name.includes("Page")) {
          fixedName = `${name} Page`;
        }

        trackPageView({ pageName: `PL Toolkit:${fixedName}`, path: pathName });
      } else if (pageType) {
        trackPageView({ pageName: `${pageType} Page`, path: pathName });
      } else {
        trackPageView({ pageName: "Home Page", path: pathName });
      }

      resolve(window.stjude.digitalData);
    }, 10);
  });
};

export default function buildRoutes() {
  return (
    <Route
      errorElement={<GlobalErrorBoundary />}
      loader={loader}
      shouldRevalidate={({ currentUrl, nextUrl }) => {
        return currentUrl !== nextUrl;
      }}
      element={<Outlet />}
      hydrateFallbackElement={<div></div>}
    >
      <Route path="/chatbot-landing" element={<TwitchChatbotCallbackPage />} />
      <Route path="/twitch-callback" element={<TwitchCallbackPage />} />
      <Route path="/tiltify-callback" element={<TiltifyCallback />} />
      <Route path="/schedule/*" element={<ScheduleLayout />}>
        <Route index element={<Schedule />} />
        <Route path=":date" element={<Schedule />} />
      </Route>
      <Route path="/*" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="ccam" element={<CCAMToolsPage />} />
        <Route path="gcx" element={<GCXSelfServicePage />} />
        <Route path="friendsgiving" element={<FriendsgivingSelfServicePage />} />
        <Route path="level-up" element={<LevelUpSelfServicePage />} />
        <Route path="ccam" element={<CCAMToolsPage />} />
        <Route path="graphics/" element={<CardPageLayout />} />
        <Route path="graphics/*" element={<GraphicsBundlePageLayout />} />
        <Route path="chatbots/" element={<ListPageLayout />} />
        <Route path="chatbots/*" element={<ChatbotPageLayout />}>
          <Route path="discord" element={<DiscordChatbotPage />} />
          <Route path="twitch" element={<TwitchChatbotPage />} />
        </Route>
        <Route path="information/" element={<ListPageLayout />} />
        <Route path="information/*" element={<InformationPageLayout />}>
          <Route path="best-practices" element={<BestPracticesPage />} />
          <Route path="matching" element={<MatchingPage />} />
          <Route path="every-dollar-makes-a-difference" element={<EveryDollarCountsPage />} />
        </Route>
        <Route path="dashboards/" element={<CardPageLayout />} />
        <Route path="dashboards/*" element={<DashboardPageLayout />}>
          <Route path="fundraising" element={<DashboardPage />} />
          <Route path="leaderboard-exclusion" element={<LeaderboardExclusionDashboardPage />} />
          <Route path="donation-train" element={<DonationTrainDashboardPage />} />
        </Route>

        <Route path="overlays/" element={<CardPageLayout />} />
        <Route path="overlays/*" element={<OverlayPageLayout />}>
          <Route path="alerts" element={<AlertsOverlayPage />} />
          <Route path="ball-pit" element={<BallPitOverlayPage />} />
          <Route path="art-space" element={<ArtSpaceOverlayPage />} />
          <Route path="ccam-ribbon" element={<CCAMRibbonOverlayPage />} />
          <Route path="donorlist" element={<DonorListOverlayPage />} />
          <Route path="leaderboard" element={<LeaderboardOverlayPage />} />
          <Route path="rewards" element={<RewardsOverlayPage />} />
          <Route path="spotlight" element={<SpotlightOverlayPage />} />
          <Route path="lifetime" element={<LifetimeRaisedPage />} />
          <Route path="polls" element={<PollOverlayPage />} />
          <Route path="milestone" element={<MilestoneOverlayPage />} />
          <Route path="total" element={<TotalOverlayPage />} />
          <Route path="donation-train" element={<DonationTrainOverlayPage />} />
          <Route path="subathon-timer" element={<SubathonTimerPage />} />
          <Route path="previous-totals" element={<PreviousTotalsOverlayPage />} />
          <Route path="gifts-that-give" element={<GiftsThatGiveOverlayPage />} />
          <Route path="schedule" element={<ScheduleOverlayPage />} />
          <Route path="tts" element={<TextToSpeechPage />} />
          <Route path="twitch-chat" element={<TwitchChatOverlayPage />} />
          <Route path="basic" element={<BasicProgressBarPage />} />
          <Route path="horizontal" element={<NewProgressBarPage />} />
          <Route path="vertical" element={<VerticalProgressBarPage />} />
          <Route path="max" element={<MaxProgressBarPage />} />
          <Route path="min" element={<MinProgressBarPage />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Route>
    </Route>
  );
}
